// @ts-nocheck
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase,
  IconChevronRight, IconBrandWhatsapp,
  IconPrinter, IconLoader2, IconX, IconMessageCircle,
} from '@tabler/icons-react';
import moment from 'moment';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import Time from '@/utils/time';
import * as Enums from '@/utils/enums';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import DownloadService from '@/utils/download-service';
import PaymentService from '@/services/payment-service';
import { getCustomerZoneItemPayments } from '@/PageComponents/Payments/payments';
import { roundPrice } from '@/utils/line-totals';
import { getGroupedItems } from '@/PageComponents/SectionTable/SectionTable';
import { StatusPill, quoteStatusDisplay, deriveStatusTone } from '../CustomerZoneShared';
import CustomerZoneMessageModal from '../CustomerZoneMessageModal';
import styles from '../CustomerZoneDetail.module.css';
import { useCzAuth } from '@/utils/useCzAuth';

export default function CustomerZoneQuoteDetail() {
  const router = useRouter();
  const { t, c } = useCzAuth();
  const { i, com, fromJobId, fromJobNum } = router.query;

  const pageContext = useContext(PageContext) as any;
  const [printing, setPrinting] = useState(false);
  const [payNowLink, setPayNowLink] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<null | 'accept' | 'decline'>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notification, setNotification] = useState<null | { msg: string; danger?: boolean }>(null);
  const notifTimer = useRef<any>(null);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: quoteData, refetch: refetchQuote } = useQuery(
    ['czQuote', t, c, i],
    () => Fetch.get({ url: '/CustomerZone/GetQuote', params: { quoteID: i }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!i }
  );

  const quote = quoteData || null;

  const linkedJobId = quote?.Module === Enums.Module.JobCard ? quote.ItemID : null;
  const { data: linkedJobData } = useQuery(
    ['czQuoteLinkedJob', t, c, linkedJobId],
    () => Fetch.get({ url: '/CustomerZone/GetJob', params: { jobCardID: linkedJobId }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!linkedJobId }
  );

  const { data: paymentsRaw } = useQuery(
    ['czQuotePayments', t, c, i],
    () => getCustomerZoneItemPayments(quote.ID, Enums.Module.Quote, t, c, tenant.API),
    { enabled: !!quote }
  );
  const payments = Array.isArray(paymentsRaw) ? paymentsRaw : (paymentsRaw?.Results || []);

  useEffect(() => {
    if (quoteData?.headerTenantName) pageContext?.updateTenantName?.(quoteData.headerTenantName);
  }, [quoteData]);

  useEffect(() => {
    if (!quote || !tenant) return;
    const totalPaid = (paymentsRaw ? (Array.isArray(paymentsRaw) ? paymentsRaw : (paymentsRaw?.Results || [])) : []).reduce((s: number, p: any) => s + +p.Amount, 0);
    const due = Math.max(0, roundPrice(+quote.TotalInclusive - totalPaid));
    PaymentService.getPayNowLink(quote.ID, quote.QuoteNumber, Enums.Module.Quote, due, t, c, tenant.API)
      .then((url: string | null) => setPayNowLink(url));
  }, [quote, tenant, paymentsRaw]);

  useEffect(() => {
    if (payNowLink && com === 'paynow' && quote?.QuoteStatus === Enums.QuoteStatus.Accepted) {
      window.location.href = payNowLink;
    }
  }, [payNowLink]);

  const isExpired = useMemo(
    () => quote?.ExpiryDate ? Time.getTimeDifference(Time.today(), quote.ExpiryDate) < 0 : false,
    [quote]
  );

  if (!tenant || !quote) return null;

  const currencySymbol = company?.Currency?.Symbol || '';
  const taxPercentage = company?.TaxPercentage;
  const waPhone = company?.WhatsAppNumber
    ? company.WhatsAppNumber.replace(/\D/g, '').replace(/^0/, '27') : null;
  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';
  const { label: statusLabel, tone } = quoteStatusDisplay(quote.QuoteStatus);
  const groupedItems = getGroupedItems(
    (quote.QuoteItems || []).map((x: any, idx: number) => ({
      ...x, groupId: x.InventorySectionID || `solo-${idx}-${x.ID}`
    })).sort((a: any, b: any) => a.LineNumber - b.LineNumber),
    'InventorySectionName', 'InventorySectionID'
  );
  const canAcceptDecline = quote.QuoteStatus === Enums.QuoteStatus.Approved;
  const linkedJob = linkedJobData?.ID ? linkedJobData : null;

  const contactName = quote.Contact
    ? `${quote.Contact.FirstName || ''} ${quote.Contact.LastName || ''}`.trim()
    : quote.CustomerName || '';

  const lessPayments = payments.reduce((s: number, p: any) => s + +p.Amount, 0);
  const amountDue = Math.max(0, roundPrice(+quote.TotalInclusive - lessPayments));

  const bankName = quote.Store?.BankName || company?.BankName;
  const bankAccountName = quote.Store?.BankAccountName || company?.BankAccountName;
  const bankAccountNumber = quote.Store?.BankAccountNumber || company?.BankAccountNumber;
  const bankBranchCode = quote.Store?.BankBranchCode || company?.BankBranchCode;
  const hasBankDetails = !!(bankName || bankAccountName || bankAccountNumber);

  const payNow = () => { if (payNowLink) window.location.href = payNowLink; };

  const showNotification = (msg: string, danger = false) => {
    setNotification({ msg, danger });
    clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => setNotification(null), 5000);
  };

  const handleDownload = async () => {
    setPrinting(true);
    try {
      await DownloadService.downloadFile(
        'GET', `/CustomerZone/GetQuoteDocument?quoteID=${quote.ID}`,
        null, true, false, t, c, tenant.API, false
      );
    } catch {
      showNotification('Could not download the document. Please try again.', true);
    } finally {
      setPrinting(false);
    }
  };

  const handleWhatsApp = () => {
    if (!waPhone) return;
    const msg = `Hi! This is ${quote.CustomerName} reaching out about quote ${quote.QuoteNumber}.`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirming(true);
    try {
      await Fetch.put({
        url: `/CustomerZone/UpdateQuoteStatus?id=${quote.ID}&quoteStatus=${action === 'accept' ? Enums.QuoteStatus.Accepted : Enums.QuoteStatus.Declined}`,
        tenantID: t, customerID: c, apiUrlOverride: tenant.API
      });
      setConfirmAction(null);
      await refetchQuote();
      showNotification(`${quote.QuoteNumber} was ${action === 'accept' ? 'accepted' : 'declined'} successfully.`);
    } catch {
      setConfirmAction(null);
      showNotification('Something went wrong sending your response. Please try again.', true);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div
        className={`${styles.topAlert}${notification ? ` ${styles.topAlertShow}` : ''}${notification?.danger ? ` ${styles.topAlertDanger}` : ''}`}
        aria-hidden={!notification}
        aria-live="polite"
      >
        <span className={styles.topAlertMsg}>{notification?.msg || ''}</span>
        <button className={styles.topAlertClose} onClick={() => setNotification(null)} aria-label="Dismiss">
          <IconX size={13} />
        </button>
      </div>

      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.cardToolbar}>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/customerzone" search={{ t, c }}>Home</Link>
                {fromJobId ? (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'jobs', expand: '1' }}>Jobs</Link>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone/viewjob" search={{ t, c, i: fromJobId }}>{decodeURIComponent(fromJobNum as string)}</Link>
                  </>
                ) : (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'quotes', expand: '1' }}>Quotes</Link>
                  </>
                )}
                <span className={styles.bcSep}><IconChevronRight /></span>
                <span className={styles.bcCurrent}>{quote.QuoteNumber}</span>
              </nav>
            </div>

        <div className={styles.cardInner}>
          <div className={styles.detail}>

            <div className={styles.detailHeader}>
              <h1 className={styles.dId}>{quote.QuoteNumber}</h1>
              <div className={styles.dActions}>
                {canAcceptDecline && (
                  <>
                    <button
                      className={`${styles.act} ${styles.actDecline}`}
                      onClick={() => setConfirmAction('decline')}
                    >
                      Decline
                    </button>
                    <button
                      className={`${styles.act} ${styles.actAccept}`}
                      onClick={() => setConfirmAction('accept')}
                    >
                      Accept
                    </button>
                  </>
                )}
                {quote.QuoteStatus === Enums.QuoteStatus.Accepted && !!payNowLink && (
                  isExpired ? (
                    <span title={`Please contact ${companyName} for additional assistance`}>
                      <button className={`${styles.act} ${styles.actPay}`} type="button" disabled>
                        <span>Pay Now</span>
                      </button>
                    </span>
                  ) : (
                    <button className={`${styles.act} ${styles.actPay}`} type="button" onClick={payNow}>
                      <span>Pay Now</span>
                    </button>
                  )
                )}
                {waPhone && (
                  <button className={styles.act} onClick={handleWhatsApp}>
                    <IconBrandWhatsapp size={14} color="#25d366" /><span>WhatsApp</span>
                  </button>
                )}
                <button className={`${styles.act} ${styles.actIconOnly}`} type="button" onClick={() => setShowMessage(true)} title="Leave a comment">
                  <IconMessageCircle size={14} />
                </button>
                <button className={styles.act} type="button" onClick={handleDownload} disabled={printing}>
                  {printing
                    ? <IconLoader2 size={14} className={styles.actSpinner} />
                    : <IconPrinter size={14} />}
                  <span>Print</span>
                </button>
              </div>
            </div>

            {linkedJob && !fromJobId && (
              <a
                className={styles.linkedJob}
                href={`/customerzone/viewjob${base}&i=${linkedJob.ID}`}
                onClick={(e) => { e.preventDefault(); router.push(`/customerzone/viewjob${base}&i=${linkedJob.ID}`); }}
              >
                <span className={styles.ljIc}>
                  <IconBriefcase size={18} />
                </span>
                <span className={styles.ljBody}>
                  <span className={styles.ljEyebrow}>Linked job</span>
                  <span className={styles.ljTitle}>
                    <span className={styles.ljId}>{linkedJob.JobCardNumber}</span>
                    {linkedJob.JobCardStatusDescription && (
                      <StatusPill text={linkedJob.JobCardStatusDescription} tone={deriveStatusTone(linkedJob.JobCardStatusDescription)} />
                    )}
                  </span>
                  {(linkedJob.JobTypeName || linkedJob.Location?.AddressLine1) && (
                    <span className={styles.ljMeta}>
                      {[linkedJob.JobTypeName, linkedJob.Location?.AddressLine1].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                <span className={styles.ljCta}>
                  <span>View job</span>
                  <IconChevronRight size={14} />
                </span>
              </a>
            )}

            {com === 'paynow' && quote.QuoteStatus !== Enums.QuoteStatus.Accepted && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#856404', margin: '0 0 8px' }}>
                This quote cannot be paid online yet. Please contact {companyName} for assistance.
              </div>
            )}

            <div className={styles.contact}>
              {contactName && <span className={styles.cName}>{contactName}</span>}
              {quote.CustomerName && contactName !== quote.CustomerName && (
                <span className={styles.cLine}>{quote.CustomerName}</span>
              )}
              {quote.Contact?.MobileNumber && <span className={styles.cLine}>{quote.Contact.MobileNumber}</span>}
              {quote.Contact?.EmailAddress && <span className={styles.cLine}>{quote.Contact.EmailAddress}</span>}
              {quote.Location?.LocationDisplay && <span className={styles.cLine}>{quote.Location.LocationDisplay}</span>}
            </div>

            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Quote Detail</h2>
                <span className={styles.panelBadge}>
                  <StatusPill text={statusLabel} tone={tone} />
                </span>
              </div>
              <div className={styles.totalSummary}>
                <span className={styles.tsLabel}>Amount Due</span>
                <span className={styles.tsValue}>{Helper.getCurrencyValue(amountDue, currencySymbol)}</span>
              </div>
              <div className={styles.qdBody}>
                <div className={styles.qdCol}>
                  {quote.Reference && (
                    <div className={styles.field}>
                      <span className={styles.fLabel}>Reference Number</span>
                      <span className={styles.fValue}>{quote.Reference}</span>
                    </div>
                  )}
                  <div className={styles.fields}>
                    {quote.QuoteDate && (
                      <div className={styles.field}>
                        <span className={styles.fLabel}>Quote Date</span>
                        <span className={styles.fValue}>{moment(quote.QuoteDate).format('D MMM YYYY')}</span>
                      </div>
                    )}
                    {quote.ExpiryDate && (
                      <div className={styles.field}>
                        <span className={styles.fLabel}>Expiry Date</span>
                        <span className={styles.fValue}>{moment(quote.ExpiryDate).format('D MMM YYYY')}</span>
                      </div>
                    )}
                  </div>
                </div>
                {quote.Comments && (
                  <div className={styles.qdCol}>
                    <div className={styles.field}>
                      <span className={styles.fLabel}>Comments</span>
                      <span className={styles.fValue} style={{ whiteSpace: 'pre-wrap' }}>{quote.Comments}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.panel}>
              <h2 className={styles.panelTitle}>Quote Items</h2>
              {groupedItems.length > 0 && (
                <div className={styles.tableWrap}>
                  <table className={styles.tbl}>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th className={styles.numRight}>Qty</th>
                        <th className={styles.numRight}>Price</th>
                        <th className={styles.tblAmtCol}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedItems.map((group: any) => (
                        <React.Fragment key={group.id}>
                          {group.isSection && !group.hideLineItems && (
                            <tr><td colSpan={5} className={styles.strong} data-label="">{group.name}</td></tr>
                          )}
                          {group.isSection && group.hideLineItems && (
                            <tr>
                              <td colSpan={4} style={{fontStyle: 'italic'}}>{group.name}</td>
                              <td className={`${styles.strong} ${styles.tblAmtCol}`}>{Helper.getCurrencyValue(group.subTotal, currencySymbol)}</td>
                            </tr>
                          )}
                          {!group.hideLineItems && group.items.map((item: any) => {
                            const isDesc = item.QuoteItemType === Enums.QuoteItemType.Description;
                            return (
                              <tr key={item.ID || item.InventoryCode}>
                                <td className={styles.code} data-label="Code">{!isDesc ? item.InventoryCode : ''}</td>
                                <td data-label="Description">{item.Description}</td>
                                <td className={styles.numRight} data-label="Quantity">{!isDesc ? item.Quantity : ''}</td>
                                <td className={styles.numRight} data-label="Price">{!isDesc ? Helper.getCurrencyValue(item.UnitPriceExclusive, currencySymbol) : ''}</td>
                                <td className={`${styles.strong} ${styles.tblAmtCol}`} data-label="Amount">{!isDesc ? Helper.getCurrencyValue(item.LineTotalExclusive, currencySymbol) : ''}</td>
                              </tr>
                            );
                          })}
                          {group.isSection && !group.hideLineItems && group.displaySubtotal && (
                            <tr>
                              <td colSpan={4} className={styles.numRight}><em>Subtotal</em></td>
                              <td className={`${styles.strong} ${styles.tblAmtCol}`}>{Helper.getCurrencyValue(group.subTotal, currencySymbol)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className={styles.totalsAlign}>
                <div className={styles.totalsCard}>
                  <div className={styles.totalsRow}>
                    <span>Subtotal Excl VAT</span>
                    <span className={styles.tAmt}>{Helper.getCurrencyValue(quote.SubTotalExclusive, currencySymbol)}</span>
                  </div>
                  {quote.DiscountPercentage > 0 && (
                    <div className={styles.totalsRow}>
                      <span>Discount ({quote.DiscountPercentage}%)</span>
                      <span className={styles.tAmt}>{Helper.getCurrencyValue(-(+quote.SubTotalExclusive - +quote.TotalExclusive), currencySymbol)}</span>
                    </div>
                  )}
                  <div className={styles.totalsRow}>
                    <span>Total Excl VAT</span>
                    <span className={styles.tAmt}>{Helper.getCurrencyValue(+quote.TotalInclusive - +quote.TotalTax, currencySymbol)}</span>
                  </div>
                  <div className={styles.totalsRow}>
                    <span>VAT {taxPercentage ? `(${taxPercentage}%)` : ''}</span>
                    <span className={styles.tAmt}>{Helper.getCurrencyValue(quote.TotalTax, currencySymbol)}</span>
                  </div>
                  <div className={`${styles.totalsRow} ${styles.totalsGrand}`}>
                    <span>Total Incl VAT</span>
                    <span className={styles.tAmt}>{Helper.getCurrencyValue(quote.TotalInclusive, currencySymbol)}</span>
                  </div>
                  {!!quote.DepositPercentage && (
                    <div className={styles.totalsRow}>
                      <span>Deposit ({quote.DepositPercentage}%)</span>
                      <span className={styles.tAmt}>{Helper.getCurrencyValue(Math.round(+quote.TotalInclusive * +quote.DepositPercentage) / 100, currencySymbol)}</span>
                    </div>
                  )}
                  {payments.length > 0 && (
                    <div className={styles.totalsRow}>
                      <span>Payments Received</span>
                      <span className={styles.tAmt}>−{Helper.getCurrencyValue(lessPayments, currencySymbol)}</span>
                    </div>
                  )}
                  <div className={styles.totalsRowDue}>
                    <span>Amount Due</span>
                    <span className={styles.totalsAmtDue}>{Helper.getCurrencyValue(amountDue, currencySymbol)}</span>
                  </div>
                </div>
              </div>
            </div>

            {payments.length > 0 && (
              <div className={styles.panel}>
                <h2 className={styles.panelTitle}>Payments</h2>
                <div className={styles.tableWrap}>
                  <table className={styles.tbl}>
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th className={styles.numRight}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p: any) => (
                        <tr key={p.ID}>
                          <td data-label="Payment Date">{p.PaymentDate ? moment(p.PaymentDate).format('D MMM YYYY') : ''}</td>
                          <td data-label="Reference">{p.Reference}</td>
                          <td data-label="Description">{p.Comment || ''}</td>
                          <td className={`${styles.strong} ${styles.numRight}`} data-label="Amount">{Helper.getCurrencyValue(p.Amount, currencySymbol)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hasBankDetails && (
              <div className={styles.panel}>
                <h2 className={styles.panelTitle}>Banking Details</h2>
                <dl className={styles.bankList}>
                  {bankName && <><dt>Bank</dt><dd>{bankName}</dd></>}
                  {bankAccountName && <><dt>Account Name</dt><dd>{bankAccountName}</dd></>}
                  {bankAccountNumber && <><dt>Account Number</dt><dd>{bankAccountNumber}</dd></>}
                  {bankBranchCode && <><dt>Branch Code</dt><dd>{bankBranchCode}</dd></>}
                  <dt>Reference</dt><dd>{quote.QuoteNumber}</dd>
                </dl>
              </div>
            )}

          </div>
        </div>
      </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}

      {showMessage && (
        <CustomerZoneMessageModal
          module={Enums.Module.Quote}
          itemID={quote.ID}
          t={t} c={c} tenant={tenant}
          onClose={() => setShowMessage(false)}
        />
      )}

      {confirmAction && (
        <div
          className={styles.modalScrim}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmAction(null); }}
        >
          <div className={styles.modal} role="dialog" aria-modal="true">
            <p className={styles.modalTitle}>
              {confirmAction === 'accept' ? 'Accept this quote?' : 'Decline this quote?'}
            </p>
            <p className={styles.modalBody}>
              {confirmAction === 'accept'
                ? `You're about to accept ${quote.QuoteNumber}. The company will be notified.`
                : `You're about to decline ${quote.QuoteNumber}. The company will be notified.`}
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.act}
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                className={`${styles.act} ${confirmAction === 'accept' ? styles.actPrimary : styles.actOutlineDanger}`}
                type="button"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming
                  ? (confirmAction === 'accept' ? 'Accepting…' : 'Declining…')
                  : (confirmAction === 'accept' ? 'Accept' : 'Decline')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
